const clamp = (value, min = 0, max = 255) => Math.min(max, Math.max(min, value));

const parseHex = (hex, fallback = { r: 112, g: 82, b: 92 }) => {
  if (typeof hex !== 'string') return fallback;

  const normalized = hex.trim().replace('#', '');
  const expanded = normalized.length === 3
    ? normalized.split('').map((character) => character.repeat(2)).join('')
    : normalized.slice(0, 6);

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return fallback;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
};

const toHex = ({ r, g, b }) => (
  `#${[r, g, b]
    .map((channel) => Math.round(clamp(channel)).toString(16).padStart(2, '0'))
    .join('')}`
);

const mix = (source, target, amount) => ({
  r: source.r * (1 - amount) + target.r * amount,
  g: source.g * (1 - amount) + target.g * amount,
  b: source.b * (1 - amount) + target.b * amount,
});

const colorDistance = (first, second) => Math.sqrt(
  ((first.r - second.r) ** 2)
  + ((first.g - second.g) ** 2)
  + ((first.b - second.b) ** 2)
);

const saturation = ({ r, g, b }) => (
  (Math.max(r, g, b) - Math.min(r, g, b)) / 255
);

export const colorWithAlpha = (hex, alpha) => {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.min(1, Math.max(0, alpha))})`;
};

export const createMutedPalette = (primaryHex, accentHex = primaryHex) => {
  const primary = parseHex(primaryHex);
  const accent = parseHex(accentHex, primary);

  return {
    deep: toHex(mix(primary, { r: 18, g: 18, b: 18 }, 0.58)),
    mid: toHex(mix(primary, { r: 75, g: 85, b: 99 }, 0.34)),
    accent: toHex(mix(accent, { r: 255, g: 255, b: 255 }, 0.22)),
    glow: toHex(mix(primary, { r: 255, g: 255, b: 255 }, 0.72)),
    soft: toHex(mix(accent, { r: 255, g: 255, b: 255 }, 0.84)),
  };
};

export const extractMediaPalette = (element) => {
  if (!element || typeof document === 'undefined') return null;

  try {
    const canvas = document.createElement('canvas');
    const sampleSize = 36;
    canvas.width = sampleSize;
    canvas.height = sampleSize;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    context.drawImage(element, 0, 0, sampleSize, sampleSize);
    const { data } = context.getImageData(0, 0, sampleSize, sampleSize);
    const buckets = new Map();

    for (let index = 0; index < data.length; index += 4) {
      const color = {
        r: data[index],
        g: data[index + 1],
        b: data[index + 2],
      };
      const alpha = data[index + 3];
      const luminance = (color.r * 0.2126) + (color.g * 0.7152) + (color.b * 0.0722);

      if (alpha < 190 || luminance < 18 || luminance > 242) continue;

      const key = `${color.r >> 5}-${color.g >> 5}-${color.b >> 5}`;
      const colorWeight = 0.8 + (saturation(color) * 1.45);
      const bucket = buckets.get(key) || {
        weight: 0,
        count: 0,
        r: 0,
        g: 0,
        b: 0,
      };

      bucket.weight += colorWeight;
      bucket.count += 1;
      bucket.r += color.r;
      bucket.g += color.g;
      bucket.b += color.b;
      buckets.set(key, bucket);
    }

    const ranked = [...buckets.values()]
      .map((bucket) => ({
        color: {
          r: bucket.r / bucket.count,
          g: bucket.g / bucket.count,
          b: bucket.b / bucket.count,
        },
        score: bucket.weight,
      }))
      .sort((first, second) => second.score - first.score);

    if (!ranked.length) return null;

    const primary = ranked[0].color;
    const secondary = ranked.find(({ color }) => colorDistance(primary, color) > 54)?.color
      || ranked[1]?.color
      || primary;

    return createMutedPalette(toHex(primary), toHex(secondary));
  } catch (error) {
    return null;
  }
};
